import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Menu, X } from 'lucide-react';

function AGIApplications() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePDFDownload = () => {
    try {
        const button = document.querySelector('[data-pdf-button]');
        button.textContent = 'Downloading...';
        button.disabled = true;

        const link = document.createElement('a');
        link.href = process.env.PUBLIC_URL + '/assets/BTD_AGI.pdf';
        link.setAttribute('download', 'BTD_AGI.pdf');
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        button.textContent = 'Downloaded!';
        setTimeout(() => {
            button.textContent = 'Download PDF';
            button.disabled = false;
        }, 2000);
    } catch (error) {
        console.error('Download error:', error);
        const button = document.querySelector('[data-pdf-button]');
        button.textContent = 'Download Failed';
        setTimeout(() => {
            button.textContent = 'Download PDF';
            button.disabled = false;
        }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
          <div className="flex items-center h-16 px-4">
            <button
              onClick={() => navigate('/btd')}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <ChevronLeft size={24} />
              <span>Back</span>
            </button>
            <h1 className="flex-1 text-center text-lg font-semibold mr-8">
              AGI Applications
            </h1>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-gray-600"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </header>
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full bg-white shadow-lg z-40 transition-all duration-300
          ${isMobile ? (isSidebarOpen ? 'w-64' : 'w-0') : 'w-64'}
          ${isMobile ? 'mt-16' : ''}
        `}
      >
        {!isMobile && (
          <div className="p-4 border-b">
            <button
              onClick={() => navigate('/btd')}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <ChevronLeft size={20} />
              <span>Back to Sections</span>
            </button>
          </div>
        )}
        
        <nav className={`${isMobile && !isSidebarOpen ? 'hidden' : 'block'} mt-4`}>
          <button
            onClick={() => {
              navigate('/btd/theory');
              isMobile && setIsSidebarOpen(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-blue-50"
          >
            BTD Theory
          </button>
          <button
            onClick={() => {
              navigate('/btd/agi');
              isMobile && setIsSidebarOpen(false);
            }}
            className="w-full px-4 py-3 text-left bg-blue-100 text-blue-700"
          >
            AGI Applications
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`
        bg-white p-8 
        ${isMobile ? 'pt-24' : 'ml-64'} 
        transition-all duration-300
      `}>
        {/* Download PDF Button */}
        <div className="relative h-14 mb-8 mobile-only">
          <button
            onClick={handlePDFDownload}
            data-pdf-button
            className="absolute top-0 right-0 px-4 py-2 bg-blue-600 text-white rounded shadow-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200 z-50"
          >
            Download PDF
          </button>
        </div>

        {/* Content container */}
        <div className="max-w-6xl mx-auto bg-white">
          <h1 className="text-4xl font-bold mb-6 md:text-4xl sm:text-3xl">
          Bidirectional Time Divergence: A Novel Approach to Temporal Processing in Advanced Artificial Intelligence
          </h1>
                
          <div className="mb-12 space-y-2">
            {/* Author and Publication Info */}
            <div className="mb-6">
              <p className="text-2xl font-semibold">Sumanth Badigeru</p>
              <p className="text-gray-600">Published: January 21, 2025</p>
            </div>
            
            {/* Contact Information */}
            <div className="flex items-center space-x-2">
              <p className="text-gray-600">Contact:</p>
              <a 
                href="https://www.linkedin.com/in/sumanth9427bb9b/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
              >
                <svg 
                  className="w-5 h-5 mr-1" 
                  fill="currentColor" 
                  viewBox="0 0 24 24" 
                  aria-hidden="true"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn Profile
              </a>
              <span className="text-gray-400">|</span>
              <a 
                href="mailto:sumanth.badigeru@gmail.com" 
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                sumanth.badigeru@gmail.com
              </a>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold mt-8 mb-4">Abstract</h2>
            <p className="mb-4 text-gray-800 leading-relaxed">
              This paper presents a novel framework for implementing Artificial General Intelligence (AGI) based on Bidirectional Time Divergence (BTD) theory hypothesis using classical AI architectures, with a focus on Transformers. We propose that human consciousness and decision-making processes exhibit properties analogous to temporal bidirectionality, which can be modeled using BTD hypothesis. By leveraging existing AI models, particularly Transformer-based architectures, and large-scale datasets, we present a practical approach to developing AGI systems that can process multiple temporal pathways simultaneously while dynamically updating causal relationships. This framework addresses the current limitations of classical AGI approaches and outlines future potential for quantum computing to enhance BTD-based systems.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-4">1. Introduction</h2>
            <h3 className="text-2xl font-semibold mb-4">1.1 Background</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The development of Artificial General Intelligence (AGI) represents one of the most significant challenges in computer science and artificial intelligence research. While current AI systems have achieved remarkable success in specific domains, they lack the temporal consciousness and bidirectional awareness characteristic of human intelligence. This paper proposes that the BTD hypothesis provides a framework for developing AGI systems that more closely approximate human cognitive processes.
            </p>

            <h3 className="text-2xl font-semibold mb-4">1.2 Motivation</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              Human consciousness exhibits remarkable capabilities in temporal processing, including:
            </p>
            <ul className="list-disc space-y-2 ml-6 mb-4">
              <li>Simultaneous awareness of past experiences and future possibilities</li>
              <li>Ability to process multiple potential outcomes in parallel</li>
              <li>Dynamic updating of causal relationships based on new information</li>
              <li>Coherent integration of temporal information into decision-making</li>
            </ul>
            <p className="mb-4 text-gray-800 leading-relaxed">
              Current classical AI systems, while powerful, process information sequentially and lack true temporal consciousness. BTD theory offers a conceptual framework for augmenting these systems with bidirectional temporal reasoning capabilities. Transformers, with their attention mechanisms and scalability, provide an ideal foundation for implementing BTD-based AGI.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-4">2. Theoretical Framework</h2>
            <h3 className="text-2xl font-semibold mb-4">2.1 BTD Theory Hypothesis and Consciousness</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              Bidirectional Time Divergence hypothesis posits that temporal evolution occurs through a network of branching pathways operating in both forward and backward directions from any causality point. Human consciousness demonstrates remarkably similar principles in its operation: when making decisions, the brain appears to simultaneously process multiple potential future outcomes while dynamically recontextualizing past experiences.
            </p>
            <p className="mb-4 text-gray-800 leading-relaxed">
              This bidirectional processing manifests in our ability to imagine alternative histories while projecting multiple future scenarios, suggesting that consciousness naturally operates within a framework of temporal divergence. AI systems enhanced with BTD theory can leverage this approach to process multiple temporal pathways simultaneously, using probabilistic modeling, attention mechanisms, and dynamic memory networks to represent and resolve these pathways.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-4">3. Human Brain Analogy</h2>
            <h3 className="text-2xl font-semibold mb-4">3.1 Temporal Processing in Neural Systems</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The human brain exhibits remarkable capabilities for processing multiple cognitive pathways in parallel. The temporal processing capabilities of neural networks demonstrate intrinsic bidirectional awareness, where past experiences and future predictions coexist in a dynamic state, allowing for sophisticated causal reasoning that dynamically updates based on new information flow through the neural network.
            </p>

            <h3 className="text-2xl font-semibold mb-4">3.2 Memory and Learning</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The human brain's memory and learning systems provide a compelling model for BTD-based AGI architectures. Neural memory operates through distributed networks where information exists across multiple pathways simultaneously, similar to how BTD theory proposes the coexistence of multiple temporal states. When humans recall memories or learn new information, the brain demonstrates remarkable bidirectional processing - past experiences influence future predictions while new experiences dynamically reorganize our understanding of past events. This natural bidirectional integration suggests that consciousness inherently processes time in both directions, supporting BTD theory's premise of temporal divergence.
            </p>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The learning process in human brains exhibits particularly relevant characteristics for BTD implementation. As new information is processed, the brain doesn't simply add linear sequences to memory; instead, it continuously reorganizes existing neural pathways while simultaneously projecting future possibilities. This mirrors BTD's concept of multiple valid histories converging to present states while diverging into potential futures. The brain's ability to maintain coherence across these temporal reorganizations while supporting parallel updates of both historical associations and predictive models provides a biological precedent for BTD's bidirectional temporal framework.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-4">4. Advantages Over Classical AGI</h2>
            <h3 className="text-2xl font-semibold mb-4">4.1 Why Transformers Are Ideal for BTD</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              Transformers, with their self-attention mechanism, excel at modeling long-range dependencies and bidirectional relationships in data. This makes them uniquely suited for implementing BTD-based AGI systems. Key advantages include:
            </p>
            <ul className="list-disc space-y-2 ml-6 mb-4">
              <li>Self-Attention for Temporal Divergence: Transformers can represent bidirectional temporal dependencies by attending to past, present, and future contexts simultaneously.</li>
              <li>Scalability: Transformers are scalable to large datasets and can efficiently process complex temporal models.</li>
              <li>Pretraining and Fine-tuning: Pretrained models like GPT or BERT can be fine-tuned for BTD-specific tasks, such as predicting multiple futures or reconstructing alternative pasts.</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-4">4.2 Enhanced Decision Making</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The BTD approach transcends classical AI limitations through probabilistic modeling of temporal pathways. Unlike classical systems that evaluate decisions sequentially, Transformers can simulate parallel exploration of multiple decision paths using their attention mechanism, leading to genuine temporal awareness and more robust decision-making capabilities.
            </p>

            <h3 className="text-2xl font-semibold mb-4">4.3 Improved Learning</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The BTD approach transcends classical AI limitations through probabilistic modeling of temporal pathways. Unlike classical systems that evaluate decisions sequentially, Transformers can simulate parallel exploration of multiple decision paths using their attention mechanism, leading to genuine temporal awareness and more robust decision-making capabilities.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-4">5. Implementation Architecture</h2>
            <h3 className="text-2xl font-semibold mb-4">5.1 Transformer-Based Processing Layer</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The Transformer-based architecture for BTD-driven AGI includes the following components:
            </p>
            <ul className="list-disc space-y-2 ml-6 mb-4">
              <li>
                <p className="font-semibold mb-2">Temporal Modeling with Self-Attention:</p>
                <ul className="list-disc ml-6">
                  <li>Encode bidirectional temporal relationships using custom embeddings that represent causality points and their forward/backward temporal branches.</li>
                </ul>
              </li>
              <li>
                <p className="font-semibold mb-2">Pretraining and Fine-Tuning:</p>
                <ul className="list-disc ml-6">
                  <li>Pretrain Transformers on large-scale temporal datasets.</li>
                  <li>Fine-tune models for BTD-specific objectives, such as predicting alternative temporal pathways or reconstructing causal chains.</li>
                </ul>
              </li>
              <li>
                <p className="font-semibold mb-2">Dynamic Memory Networks:</p>
                <ul className="list-disc ml-6">
                  <li>Use memory networks to store and retrieve information about past and potential future states, enabling real-time temporal reasoning.</li>
                </ul>
              </li>
              <li>
                <p className="font-semibold mb-2">Probabilistic Decision Modeling:</p>
                <ul className="list-disc ml-6">
                  <li>Implement probabilistic decoding techniques (e.g., beam search or sampling) to output multiple plausible temporal paths for a given input.</li>
                </ul>
              </li>
            </ul>
            <div className="mb-8">
              <img 
                src="/images/Figure_4.png" 
                alt="Figure 4: Temporal Divergence Layer On Transformers" 
                className="w-full sm:w-[250px] lg:w-[600px] h-auto mx-auto mb-4 shadow-md rounded-lg"
              />
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-4">6. Practical Applications</h2>
            <h3 className="text-2xl font-semibold mb-4">6.1 Near-term Implementation</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              Implementation of BTD-based AI systems will follow a progressive development pathway, beginning with enhanced decision optimization and temporal prediction systems based on Transformers. These initial applications will demonstrate capabilities beyond classical AI limits.
            </p>

            <h3 className="text-2xl font-semibold mb-4">6.2 Scalability</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              The modular design of Transformer-based architectures ensures scalability through incremental upgrades. This approach enables efficient processing of large-scale datasets and complex temporal models while maintaining operational efficiency.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-4">7. Future Improvisation</h2>
            <h3 className="text-2xl font-semibold mb-4">7.1 Role of Quantum Computing</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              While this paper focuses on Transformer-based AI systems, quantum computing presents a promising avenue for future improvisation. Quantum systems inherently support superposition and parallelism, which align closely with BTD theory's principles of temporal divergence. The potential advantages of quantum computing for BTD-based AGI include:
            </p>
            <ul className="list-disc space-y-2 ml-6 mb-4">
              <li>Exponential Speedups: Quantum algorithms could process vast decision spaces exponentially faster than classical methods.</li>
              <li>True Temporal Superposition: Unlike probabilistic modeling, quantum systems could natively represent and compute across multiple temporal states simultaneously.</li>
              <li>Enhanced Scalability: Scalable quantum hardware could enable more complex implementations of BTD-based AGI frameworks.</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-4">7.2 Integration with Transformers</h3>
            <p className="mb-4 text-gray-800 leading-relaxed">
              Future systems may combine quantum and Transformer-based components to leverage the strengths of both paradigms. For example, classical Transformers could handle large-scale data preprocessing, while quantum layers optimize temporal reasoning and decision-making processes.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-4">8. References</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">[1] Consciousness in the universe: A review of the 'Orch OR' theory</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Hameroff, S., & Penrose, R. "Consciousness in the universe: A review of the 'Orch OR' theory." Physics of Life Reviews, 2014.</li>
                  <li>This paper explores the quantum nature of consciousness, suggesting mechanisms like quantum superposition in neural processes. It parallels the analogy between BTD and the brain's bidirectional processing, supporting your claims about human cognition as a model for AGI.</li>
                  <li>
                    <a 
                      href="https://www.sciencedirect.com/science/article/abs/pii/S1571064513001188"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Read the full paper
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">[2] Quantum Error Correction Using Surface Codes</h3>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Google AI Blog. "Suppressing quantum errors by scaling a surface code logical qubit" 2023.</li>
                  <li>This blog outlines Google's advancements in quantum error correction, a critical aspect of achieving scalable quantum systems. It provides context for addressing hardware challenges in implementing the quantum layer of the BTD-AGI framework.</li>
                  <li>
                    <a 
                      href="https://www.nature.com/articles/s41586-022-05434-1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Read the full article
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-30 mobile-only"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default AGIApplications;